/// <reference lib="webworker" />
import { precacheAndRoute } from "workbox-precaching";
const wbManifest = self.__WB_MANIFEST;
const precacheManifest = Array.isArray(wbManifest)
  ? wbManifest
  : [];
precacheAndRoute(precacheManifest);
console.log("webworker started");

import { buildBasicAuthorizationHeader } from "@/utils/auth";

// Minimal state type for service worker (replacing overmind RootState)
interface SwState {
  apiToken?: string;
  apiUrl?: string;
  user: {
    contactsById: Record<string, { username?: string }>;
  };
}
import { ErrorLogger } from "@/utils/module_api";
const DEV_IP = import.meta.env.VITE_DEV_IP_ADDR;
const FALLBACK_API_URL = import.meta.env.VITE_API_URL as string | undefined;

const isIpAddress = (str: string): boolean =>
  /^(\d{1,3}\.){3}\d{1,3}$/.test(str); // matches IPv4 only

let apiBaseUrl: string | undefined;
if (DEV_IP === "localhost" || isIpAddress(DEV_IP)) {
  apiBaseUrl = `https://${DEV_IP}:3001/api`;
} else {
  apiBaseUrl = FALLBACK_API_URL;
}

declare const self: ServiceWorkerGlobalScope;

let currentState: SwState | null = null;
const swHost = self.location.hostname.replace(/[^a-z0-9.-]/gi, "_");

// IndexedDB helpers for persistence
const DB_NAME = `sw-state-db:${swHost}`;
const STORE_NAME = "overmind-state";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function persistState(state: SwState): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(state, "current");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function retrieveState(): Promise<SwState | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get("current");
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

self.addEventListener("message", (event) => {
  const { type, payload } = event.data;

  if (type === "OVERMIND_STATE_UPDATE" || type === "STATE_UPDATE") {
    currentState = JSON.parse(payload.state) as SwState;
    if (currentState.apiUrl) {
      apiBaseUrl = currentState.apiUrl;
    }
    persistState(currentState)
      .then(() => {
        console.log("[Service Worker] State persisted to IndexedDB");
      })
      .catch((error) => {
        ErrorLogger.logError(error, "sw.ts");
        console.error("[Service Worker] Error persisting state:", error);
      });

    console.log("[Service Worker] State updated:", currentState);
  }
});

type FetchedPushMessage = {
  id?: string;
  senderId?: string;
  sender_id?: string;
  receiverId?: string;
  receiver_id?: string;
  threadId?: string;
  thread_id?: string;
  roomId?: string;
  room_id?: string;
};

function valueAsFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function valueAsNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

async function fetchMessage(messageHandle: number): Promise<FetchedPushMessage> {
  const resolvedApiBaseUrl = currentState?.apiUrl || apiBaseUrl;
  if (!resolvedApiBaseUrl) {
    throw new Error("Missing API base URL for service worker fetch");
  }

  const response = await fetch(`${resolvedApiBaseUrl}/fetch-message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: buildBasicAuthorizationHeader(currentState?.apiToken || ""),
    },
    body: JSON.stringify({ messageHandle }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch message: ${response.statusText}`);
  }

  const data = await response.json();
  return data.message || {};
}

function extractPushData(payload: any): Record<string, unknown> {
  if (payload && typeof payload.data === "object" && payload.data !== null) {
    return payload.data;
  }
  return payload && typeof payload === "object" ? payload : {};
}

function parseCurrentUserIdFromToken(token: string | undefined): string | undefined {
  if (!token) return undefined;
  const payload = token.split(".")[1];
  if (!payload) return undefined;

  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = JSON.parse(atob(padded)) as { user_id?: string; userId?: string };
    const userId = typeof decoded.user_id === "string"
      ? decoded.user_id
      : typeof decoded.userId === "string"
        ? decoded.userId
        : undefined;
    const trimmed = userId?.trim();
    return trimmed ? trimmed : undefined;
  } catch (error) {
    console.warn("[Service Worker] Failed to decode current user from token", error);
    return undefined;
  }
}

async function setServiceWorkerAppBadge(count = 1): Promise<void> {
  const registration = self.registration as ServiceWorkerRegistration & {
    setAppBadge?: (contents?: number) => Promise<void>;
  };
  try {
    await registration.setAppBadge?.(count);
  } catch (error) {
    console.debug("[Service Worker] App badge update skipped", error);
  }
}

self.addEventListener("install", () => {
  console.log("[Service Worker] Installing...");
  self.skipWaiting();
});

self.addEventListener("activate", (event) => {
  console.log("[Service Worker] Activating...");
  event.waitUntil(
    (async () => {
      currentState = (await retrieveState()) || null;
      if (currentState?.apiUrl) {
        apiBaseUrl = currentState.apiUrl;
      }
      console.log("[Service Worker] Loaded persisted state:", currentState);
      await self.clients.claim();
    })(),
  );
});

self.addEventListener("push", (event) => {
  console.log("[Service Worker] Push received!");

  if (!event.data) {
    console.warn("[Service Worker] No push data.");
    return;
  }

  // Log raw push data for debugging
  const rawData = event.data.text();
  console.log("[Service Worker] Raw push data:", rawData);

  event.waitUntil(
    (async () => {
      try {
        const payload = event.data?.json();
        console.log("[Service Worker] Parsed payload:", payload);

        const pushData = extractPushData(payload);
        const messageHandle = valueAsFiniteNumber(pushData.messageHandle);
        const payloadMessageId = valueAsNonEmptyString(pushData.messageId);
        const payloadSenderId = valueAsNonEmptyString(pushData.senderId);
        console.log("[Service Worker] Message handle:", messageHandle);

        if (typeof messageHandle === "number" && Number.isFinite(messageHandle)) {
          const message = await fetchMessage(messageHandle).catch((error): FetchedPushMessage => {
            console.warn("[Service Worker] Message metadata fetch failed; showing generic notification", error);
            return {};
          });
          const currentUserId = parseCurrentUserIdFromToken(currentState?.apiToken);
          const senderId =
            valueAsNonEmptyString(message.senderId) ||
            valueAsNonEmptyString(message.sender_id) ||
            payloadSenderId ||
            "";

          if (currentUserId && senderId && currentUserId === senderId) {
            console.log("[Service Worker] Suppressing self-sent push notification", {
              messageHandle,
              senderId,
              currentUserId,
            });
            return;
          }

          const tagMessageId = valueAsNonEmptyString(message.id) || payloadMessageId || String(messageHandle);
          await self.registration.showNotification("New Message", {
            body: "You have received a message",
            icon: "/app-splash.png",
            badge: "/app-splash.png",
            tag: `message-${tagMessageId}`,
            requireInteraction: false,
          } as NotificationOptions);
          await setServiceWorkerAppBadge(1);

          console.log("[Service Worker] Notification shown after message fetch");
          return;
        }

        // Show OS notification immediately
        await self.registration.showNotification("New Message", {
          body: "You have received a message",
          icon: "/app-splash.png",
          badge: "/app-splash.png",
          tag: `message-${payloadMessageId || Date.now()}`,
          requireInteraction: false,
        } as NotificationOptions);
        await setServiceWorkerAppBadge(1);

        console.log("[Service Worker] Notification shown");
      } catch (error) {
        console.error("[Service Worker] Error processing push:", error);
        // Show a fallback notification
        await self.registration.showNotification("New Message", {
          body: "You have received a message",
          tag: `push-${Date.now()}`,
        });
        await setServiceWorkerAppBadge(1);
      }
    })()
  );
});

self.addEventListener("notificationclick", (event) => {
  event.notification.close();
  event.waitUntil(self.clients.openWindow("/"));
});

export {};
